// Tracking failures + recipient edit + stuck-112 recovery — pure rules.
// Spec docs/specs/2026-10-14-recipient-phone-tracking-failures-design.md §3.2 / §5.3.
// Every API field read here is additive: an older API leaves it undefined and
// the UI then shows no badge / banner (never a fake 0).

export const PLATFORM_STOREFRONT = 9;
export const PAYMENT_TYPE_COD = 2;
export const RETRY_MAX_ORDERS = 100;
export const REVERT_MAX_ORDERS = 200;
export const EDITABLE_STATUSES = [111, 112];
export const STATUS_AWAITING_PICKUP = 112;
// Re-read the seller failures banner this long after a cut-off: tracking is
// minted in the background (Seller_cutoff.go sellerRoundGenerateTracking).
export const BANNER_REFETCH_MS = 20000;

export const REASON_CODES = [
  'phone_invalid', 'recipient_name_invalid', 'address_invalid', 'area_invalid',
  'postcode_invalid', 'carrier_rejected', 'carrier_auth', 'carrier_unavailable',
  'no_tracking_returned', 'no_provider', 'internal', 'barcode_not_saved', 'unknown'
];

// The carrier issued (and bills) a barcode but saving it to the order failed
// (bo-api 099514b1). Never retried — a SuperAdmin attaches the issued barcode.
export const REASON_BARCODE_NOT_SAVED = 'barcode_not_saved';

export function isBarcodeNotSaved(row) {
  return !!row && row.reason_code === REASON_BARCODE_NOT_SAVED;
}

/** Rows the retry may book: everything except barcode_not_saved. */
export function retryableRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r => !!r && !isBarcodeNotSaved(r));
}

/** Trim + uppercase, the same as PUT /orders/tracking-attach/:id. */
export function normalizeTrackingNumber(v) {
  return String(v == null ? '' : v).trim().toUpperCase();
}

/** '' = ok, else the i18n key (API: required, ≤ 45 chars). */
export function trackingNumberError(v) {
  const tn = normalizeTrackingNumber(v);
  return tn === '' || tn.length > 45 ? 'errors.tracking_number_invalid' : '';
}

/**
 * The barcode inside carrier_message ("barcode EF123456789TH was issued by the
 * carrier but not saved to the order"), '' when none is parsable.
 */
export function barcodeFromMessage(msg) {
  const s = String(msg == null ? '' : msg);
  const named = s.match(/\bbarcode\s+([A-Za-z0-9-]{4,45})\b/i);
  if (named) return normalizeTrackingNumber(named[1]);
  const tp = s.match(/\b[A-Za-z]{2}\d{9}[A-Za-z]{2}\b/);
  return tp ? normalizeTrackingNumber(tp[0]) : '';
}

// --- phone (D1) -------------------------------------------------------------

const THAI_DIGIT_ZERO = 0x0e50; // ๐

export function normalizePhone(input) {
  let d = String(input == null ? '' : input)
    .replace(/[๐-๙]/g, c => String(c.charCodeAt(0) - THAI_DIGIT_ZERO))
    .replace(/\D/g, '');
  if (d.length === 11 && d.indexOf('66') === 0) d = '0' + d.slice(2);
  return d;
}

export function isThaiMobile(input) {
  return /^0[689]\d{8}$/.test(normalizePhone(input));
}

/**
 * The recipient phone rule the API applies on PUT /orders/recipient/:id:
 * platform 9 (storefront) = Thai mobile (D1); other platforms = 9–10 digits
 * starting with 0 (marketplace orders may carry a landline).
 */
export function isRecipientPhoneValid(input, platformId) {
  if (Number(platformId) === PLATFORM_STOREFRONT) return isThaiMobile(input);
  return /^0\d{8,9}$/.test(normalizePhone(input));
}

/** 0xx-xxx-xxxx for 10 digits, 0x-xxx-xxxx for a 9-digit landline. */
export function formatPhone(input) {
  const d = normalizePhone(input).slice(0, 10);
  if (d.length === 9 && d[1] !== '6' && d[1] !== '8' && d[1] !== '9') {
    return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
  }
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/** What the table shows: formatted when it is digits, the raw text otherwise ("test"). */
export function displayPhone(input) {
  const raw = String(input == null ? '' : input).trim();
  if (!raw) return '';
  const d = normalizePhone(raw);
  return d.length >= 9 && d.length <= 10 && /^[\d\s\-+().๐-๙]+$/.test(raw) ? formatPhone(d) : raw;
}

// --- envelopes ----------------------------------------------------------------

/** `{data:{…}}` (spec) | `{Datum:{…}}` (older handlers) → the inner object. */
export function payloadOf(resp) {
  const body = resp && resp.data !== undefined ? resp.data : resp;
  if (!body || typeof body !== 'object') return {};
  if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) return body.data;
  if (body.Datum && typeof body.Datum === 'object' && !Array.isArray(body.Datum)) return body.Datum;
  return body;
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v) {
  return v == null ? '' : String(v);
}

/** GET …/tracking-failures → { tableReady, rows, total }. */
export function normalizeFailures(resp) {
  const p = payloadOf(resp);
  const rows = (Array.isArray(p.rows) ? p.rows : []).map(r => {
    const row = r || {};
    const code = REASON_CODES.indexOf(row.reason_code) >= 0 ? row.reason_code : 'unknown';
    return Object.assign({}, row, {
      bo_order_id: num(row.bo_order_id || row.id),
      bo_packing_id: num(row.bo_packing_id),
      platform_id: num(row.platform_id),
      reason_code: code,
      carrier_message: str(row.carrier_message),
      attempts: num(row.attempts),
      // The API computes it; fall back to the D1/platform rule when absent.
      recipient_phone_valid: typeof row.recipient_phone_valid === 'boolean'
        ? row.recipient_phone_valid
        : isRecipientPhoneValid(row.recipient_phone, row.platform_id)
    });
  });
  return { tableReady: p.table_ready !== false, rows, total: num(p.total) || rows.length };
}

/** POST …/tracking-retry → counts + failures[]. */
export function normalizeRetryResult(resp) {
  const p = payloadOf(resp);
  return {
    orderNoTracking: num(p.order_no_tracking),
    minted: num(p.minted),
    failed: num(p.failed),
    skippedNoProvider: num(p.skipped_no_provider),
    skippedHasTracking: num(p.skipped_has_tracking),
    skippedBarcodeNotSaved: num(p.skipped_barcode_not_saved),
    barcodeNotSavedIds: Array.isArray(p.barcode_not_saved_ids) ? p.barcode_not_saved_ids.map(num) : [],
    failures: Array.isArray(p.failures) ? p.failures : []
  };
}

/** SuperAdmin GET /packings/tracking: Data[0] is the packingTrackingResult. */
export function regenSkippedBarcodeNotSaved(resp) {
  const body = resp && resp.data;
  const first = body && Array.isArray(body.Data) ? body.Data[0] : null;
  return first ? num(first.skipped_barcode_not_saved) : 0;
}

/** Sum several retry results (the seller banner may span rounds). */
export function sumRetryResults(list) {
  return (list || []).reduce((acc, r) => ({
    orderNoTracking: acc.orderNoTracking + r.orderNoTracking,
    minted: acc.minted + r.minted,
    failed: acc.failed + r.failed,
    skippedNoProvider: acc.skippedNoProvider + r.skippedNoProvider,
    skippedHasTracking: acc.skippedHasTracking + r.skippedHasTracking,
    skippedBarcodeNotSaved: acc.skippedBarcodeNotSaved + (r.skippedBarcodeNotSaved || 0),
    barcodeNotSavedIds: acc.barcodeNotSavedIds.concat(r.barcodeNotSavedIds || []),
    failures: acc.failures.concat(r.failures)
  }), { orderNoTracking: 0, minted: 0, failed: 0, skippedNoProvider: 0, skippedHasTracking: 0,
    skippedBarcodeNotSaved: 0, barcodeNotSavedIds: [], failures: [] });
}

/**
 * Selected failure rows → one retry request per round, ≤ RETRY_MAX_ORDERS ids
 * each: [{ bo_packing_id, order_ids }]. Rows without a round, and
 * barcode_not_saved rows (a second booking = a second paid barcode), are skipped.
 */
export function retryBatches(rows) {
  const byRound = {};
  const order = [];
  retryableRows(rows).forEach(r => {
    const pid = num(r && r.bo_packing_id);
    const oid = num(r && r.bo_order_id);
    if (!pid || !oid) return;
    if (!byRound[pid]) { byRound[pid] = []; order.push(pid); }
    if (byRound[pid].indexOf(oid) < 0) byRound[pid].push(oid);
  });
  const out = [];
  order.forEach(pid => {
    const ids = byRound[pid];
    for (let i = 0; i < ids.length; i += RETRY_MAX_ORDERS) {
      out.push({ bo_packing_id: pid, order_ids: ids.slice(i, i + RETRY_MAX_ORDERS) });
    }
  });
  return out;
}

/** GET …/stuck-no-round → { rows, total }. */
export function normalizeStuck(resp) {
  const p = payloadOf(resp);
  const rows = Array.isArray(p.rows) ? p.rows : [];
  return { rows, total: num(p.total) || rows.length };
}

/** POST …/revert-awaiting-shipment → { requested, reverted }. */
export function normalizeRevertResult(resp) {
  const p = payloadOf(resp);
  return { requested: num(p.requested), reverted: num(p.reverted) };
}

/** The packing round's `no_tracking_count` (null when the API does not send it). */
export function noTrackingCount(row) {
  if (!row || typeof row !== 'object') return null;
  const v = row.no_tracking_count !== undefined ? row.no_tracking_count : row.NoTrackingCount;
  if (v === undefined || v === null || v === '') return null;
  return num(v);
}

export function reasonKey(code) {
  return 'tracking_failure.reason.' + (REASON_CODES.indexOf(code) >= 0 ? code : 'unknown');
}

// --- order rows ----------------------------------------------------------------

function trackingOf(row) {
  return str(row && row.shipping_tracking_number).trim();
}

export function orderIdOf(row) {
  return num(row && (row.bo_order_id || row.ID || row.id));
}

export function isCodOrder(row) {
  if (!row) return false;
  const v = [row.payment_method_type, row.bo_payment_method_type_id, row.payment_method_type_id]
    .find(x => x !== undefined && x !== null && x !== '');
  return num(v) === PAYMENT_TYPE_COD;
}

/** 112 with no packing round and no tracking — the "stuck" orders (spec §2 D). */
export function isStuckNoRound(row) {
  return !!row && num(row.order_status) === STATUS_AWAITING_PICKUP && num(row.bo_packing_id) === 0 &&
    !trackingOf(row);
}

/**
 * Stuck-list rows the revert may still move: the API flags `revertable`
 * (false for a row that already has a tracking number); a row carrying a
 * tracking number / has_tracking is never revertable even without the flag.
 */
export function revertableRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(r =>
    !!r && r.revertable !== false && !r.has_tracking && !trackingOf(r));
}

/** The recipient can be edited: 111/112 and no tracking number yet. */
export function canEditRecipient(row) {
  return !!row && EDITABLE_STATUSES.indexOf(num(row.order_status)) >= 0 && !trackingOf(row);
}

/**
 * Extra ⋮ items for the new staff orders list (kept out of
 * ordersNew.rowActions so its pinned set does not move). roles: { isFullOrder,
 * isFullPayment } — the API gates are mw.IsFullOrder / mw.IsFullPayment
 * (role 7 or SuperAdmin); a partial role gets 403 access_denied (permission
 * dialog, bo-api #937), so the item is not offered to it.
 */
export function recoveryActions(row, roles) {
  const r = roles || {};
  const out = [];
  if (canEditRecipient(row) && r.isFullOrder) {
    out.push({ key: 'edit_recipient', labelKey: 'tracking_failure.edit_recipient', icon: 'fa fa-address-card' });
  }
  if (isStuckNoRound(row) && r.isFullPayment) {
    out.push({ key: 'revert_awaiting', labelKey: 'tracking_failure.revert', icon: 'fa fa-undo' });
  }
  return out;
}

/** Order / failure row → the dialog's form. */
export function recipientFormFrom(row) {
  const o = row || {};
  return {
    recipient_name: str(o.recipient_name),
    recipient_phone: str(o.recipient_phone),
    recipient_address_detail: str(o.recipient_address_detail),
    // Prefilled with the stored DOPA ids; a picked area replaces them.
    recipient_address_district_id: num(o.recipient_address_district_id),
    recipient_address_district: str(o.recipient_address_district),
    recipient_address_city_id: num(o.recipient_address_city_id),
    recipient_address_city: str(o.recipient_address_city),
    recipient_address_province_id: num(o.recipient_address_province_id),
    recipient_address_province: str(o.recipient_address_province),
    recipient_zipcode: str(o.recipient_zipcode)
  };
}

/** Field → i18n error key ('' = ok), same rules as the API (§3.2 #3). */
export function validateRecipient(form, platformId) {
  const f = form || {};
  const errs = {};
  if (!str(f.recipient_name).trim()) errs.recipient_name = 'errors.recipient_required';
  if (!isRecipientPhoneValid(f.recipient_phone, platformId)) {
    errs.recipient_phone = Number(platformId) === PLATFORM_STOREFRONT
      ? 'tracking_failure.phone_mobile_invalid'
      : 'errors.recipient_phone_invalid';
  }
  if (!str(f.recipient_address_detail).trim()) errs.recipient_address_detail = 'errors.recipient_required';
  if (!str(f.recipient_address_district).trim() || !str(f.recipient_address_city).trim() ||
      !str(f.recipient_address_province).trim()) {
    errs.recipient_area = 'errors.recipient_required';
  }
  if (!/^\d{5}$/.test(str(f.recipient_zipcode).trim())) errs.recipient_zipcode = 'errors.recipient_postcode_invalid';
  return errs;
}

/**
 * The PUT body: trimmed, phone normalised. The three area ids are int64 on the
 * API, so they are always numbers; an id that is unknown (0) is OMITTED and the
 * API keeps the stored one (never "" — that fails the bind with 400).
 */
export function recipientPayload(form) {
  const f = form || {};
  const body = {
    recipient_name: str(f.recipient_name).trim(),
    recipient_phone: normalizePhone(f.recipient_phone),
    recipient_address_detail: str(f.recipient_address_detail).trim(),
    recipient_address_district: str(f.recipient_address_district).trim(),
    recipient_address_city: str(f.recipient_address_city).trim(),
    recipient_address_province: str(f.recipient_address_province).trim(),
    recipient_zipcode: str(f.recipient_zipcode).trim()
  };
  ['recipient_address_district_id', 'recipient_address_city_id', 'recipient_address_province_id'].forEach(k => {
    const n = Math.trunc(num(f[k]));
    if (n > 0) body[k] = n;
  });
  return body;
}

/**
 * The revert's own wording for a refusal: errors.order_has_tracking reads
 * "…แก้ไขที่อยู่ไม่ได้" (written for the recipient edit), so the revert maps it to
 * tracking_failure.revert_has_tracking.
 */
export function revertErrorKey(key) {
  return key === 'errors.order_has_tracking' ? 'tracking_failure.revert_has_tracking' : key;
}

/** attach-tracking refusals: order_has_tracking gets the attach wording. */
export function attachErrorKey(key) {
  return key === 'errors.order_has_tracking' ? 'tracking_failure.attach_has_tracking' : key;
}

/** Failure / order time → "YYYY-MM-DD HH:mm" in Bangkok time ('' when absent). */
export function formatFailureTime(value) {
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value);
  // No zone ("YYYY-MM-DD HH:mm[:ss]" or "…THH:mm:ss[.fff]") = Asia/Bangkok wall
  // clock (the BO API's DB time): keep its digits — never let new Date() read it
  // as the browser's local time.
  const wall = raw.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(:\d{2}(\.\d+)?)?$/);
  if (wall) return `${wall[1]} ${wall[2]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const t = new Date(d.getTime() + 7 * 3600 * 1000);
  const p = n => String(n).padStart(2, '0');
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())} ${p(t.getUTCHours())}:${p(t.getUTCMinutes())}`;
}

export default {
  PLATFORM_STOREFRONT, PAYMENT_TYPE_COD, RETRY_MAX_ORDERS, REVERT_MAX_ORDERS, EDITABLE_STATUSES,
  STATUS_AWAITING_PICKUP, BANNER_REFETCH_MS, REASON_CODES,
  normalizePhone, isThaiMobile, isRecipientPhoneValid, formatPhone, displayPhone,
  payloadOf, normalizeFailures, normalizeRetryResult, sumRetryResults, retryBatches,
  normalizeStuck, normalizeRevertResult, noTrackingCount, reasonKey,
  orderIdOf, isCodOrder, isStuckNoRound, revertableRows, canEditRecipient, recoveryActions,
  formatFailureTime, revertErrorKey,
  REASON_BARCODE_NOT_SAVED, isBarcodeNotSaved, retryableRows, normalizeTrackingNumber,
  trackingNumberError, barcodeFromMessage, regenSkippedBarcodeNotSaved, attachErrorKey,
  recipientFormFrom, validateRecipient, recipientPayload
};
